import type { Typed } from 'services/api/fields/core';

import { LocationEpisodeLink, LocationEpisodeLinkOptions } from './LocationEpisodeLink';

export const LINK_SET_STATUSES = {
  LINKED: 'linked',
  PENDING: 'pending',
  UNLINKED: 'unlinked',
} as const;

export type LinkSetStatuses = (typeof LINK_SET_STATUSES)[keyof typeof LINK_SET_STATUSES];

export interface LocationEpisodeLinkSetOptions {
  id: string;
  groupName: string;
  patientName: string;
  archived: boolean;
  createdAt: string;
  updatedAt: string;
  locationEpisodeLinks?: LocationEpisodeLinkOptions[];
  [key: string]: any;
}

function getDefaults(): LocationEpisodeLinkSetOptions {
  return {
    id: '',
    groupName: '',
    patientName: '',
    archived: false,
    createdAt: '',
    updatedAt: '',
    locationEpisodeLinks: [],
  };
}

let LocationEpisodeLinkSet: new <T extends LocationEpisodeLinkSetOptions>(
  options: T
) => Typed<_LocationEpisodeLinkSet<T>, T>;

class _LocationEpisodeLinkSet<T extends LocationEpisodeLinkSetOptions = LocationEpisodeLinkSetOptions> {
  archived!: boolean;
  locationEpisodeLinks!: LocationEpisodeLink<NonNullable<T['locationEpisodeLinks']>[number]>[];

  constructor(options: T) {
    const opts = { ...getDefaults(), ...options };
    Object.assign(this, opts);

    this.archived = opts.archived;
    this.locationEpisodeLinks = (opts.locationEpisodeLinks ?? []).map(
      (o) => new LocationEpisodeLink<NonNullable<T['locationEpisodeLinks']>[number]>(o)
    );
  }

  get linkCount() {
    return this.locationEpisodeLinks.length;
  }

  get status() {
    if (this.archived) return LINK_SET_STATUSES.UNLINKED;
    return this.locationEpisodeLinks.every((lel) => lel.isActive)
      ? LINK_SET_STATUSES.LINKED
      : LINK_SET_STATUSES.PENDING;
  }

  get isLinked() {
    return this.status === LINK_SET_STATUSES.LINKED;
  }

  get isPending() {
    return this.status === LINK_SET_STATUSES.PENDING;
  }

  get isUnlinked() {
    return this.status === LINK_SET_STATUSES.UNLINKED;
  }

  get activeLinks() {
    return this.locationEpisodeLinks.filter((link) => link.isActive && !link.archived);
  }

  get pendingLinks() {
    return this.locationEpisodeLinks.filter((link) => link.isPending && !link.archived);
  }

  get hasNewMatchForExistingLink() {
    return this.activeLinks.length > 0 && this.pendingLinks.length > 0;
  }
}

// eslint-disable-next-line prefer-const
LocationEpisodeLinkSet = _LocationEpisodeLinkSet as typeof LocationEpisodeLinkSet;

export default LocationEpisodeLinkSet;
export { LocationEpisodeLinkSet };
export type LocationEpisodeLinkSet<T extends LocationEpisodeLinkSetOptions = LocationEpisodeLinkSetOptions> =
  _LocationEpisodeLinkSet<T> & Omit<T, keyof _LocationEpisodeLinkSet<T>>;
